// src/lib/exif-light.js — extraction EXIF MINIMALE, sans dépendance.
//
// POURQUOI (refonte Incidents, GO Dimitri 2026-08-13) : une photo de preuve
// perd sa valeur si on ne peut plus dire QUAND elle a été prise et PAR QUEL
// appareil — or la compression canvas STRIP les EXIF (constat mesuré :
// `compressImage` redessine les pixels, les métadonnées ne survivent pas).
// La doctrine « l'original d'abord » (≤ 8 MB envoyé brut) préserve les EXIF en
// base ; ce module les EXTRAIT AVANT toute compression pour que la date et
// l'appareil survivent MÊME quand le repli compressé s'impose.
//
// PÉRIMÈTRE VOLONTAIREMENT ÉTROIT : JPEG seulement (les photos de téléphone),
// trois tags — DateTimeOriginal (0x9003), Make (0x010F), Model (0x0110).
// Tout échec rend { date: null, appareil: null } : l'extraction est un bonus
// de preuve, jamais un obstacle au signalement.

const SOI = 0xffd8
const APP1 = 0xffe1
const TAG_MAKE = 0x010f
const TAG_MODEL = 0x0110
const TAG_EXIF_IFD = 0x8769
const TAG_DATETIME_ORIGINAL = 0x9003
// Les EXIF vivent dans les premiers segments — 256 KB couvrent tous les cas
// réels sans charger un original de 8 MB en mémoire pour rien.
const TETE_MAX = 256 * 1024

/**
 * Extrait { date, appareil } d'un fichier image.
 * @param {File|Blob} file
 * @returns {Promise<{date: string|null, appareil: string|null}>}
 *   date = "YYYY-MM-DD HH:MM:SS" (normalisée depuis "YYYY:MM:DD HH:MM:SS")
 *   appareil = "Make Model" dédupliqué (ex. "Apple iPhone 14")
 */
export async function extraireExif(file) {
  const vide = { date: null, appareil: null }
  try {
    if (!file || typeof file.slice !== 'function') return vide
    const buf = await file.slice(0, TETE_MAX).arrayBuffer()
    const dv = new DataView(buf)
    if (dv.byteLength < 4 || dv.getUint16(0) !== SOI) return vide

    // Balayage des segments JPEG jusqu'à l'APP1 "Exif".
    let offset = 2
    while (offset + 4 <= dv.byteLength) {
      const marker = dv.getUint16(offset)
      if ((marker & 0xff00) !== 0xff00) return vide // flux corrompu
      const taille = dv.getUint16(offset + 2)
      if (marker === APP1 && offset + 10 <= dv.byteLength) {
        // "Exif\0\0" ?
        const exifSig =
          dv.getUint32(offset + 4) === 0x45786966 && dv.getUint16(offset + 8) === 0x0000
        if (exifSig) return lireTiff(dv, offset + 10)
      }
      // SOS (début des données image) : plus aucun EXIF au-delà.
      if (marker === 0xffda) return vide
      offset += 2 + taille
    }
    return vide
  } catch (_) {
    return vide
  }
}

function lireTiff(dv, tiff) {
  const vide = { date: null, appareil: null }
  if (tiff + 8 > dv.byteLength) return vide
  const ordre = dv.getUint16(tiff)
  const le = ordre === 0x4949 // 'II' = little-endian ; 'MM' = big-endian
  if (!le && ordre !== 0x4d4d) return vide
  const u16 = (o) => dv.getUint16(o, le)
  const u32 = (o) => dv.getUint32(o, le)
  if (u16(tiff + 2) !== 0x002a) return vide

  let make = null
  let model = null
  let date = null

  const lireAscii = (entree) => {
    const count = u32(entree + 4)
    if (count <= 0 || count > 256) return null
    // ≤ 4 octets : la valeur vit DANS le champ ; sinon c'est un offset TIFF.
    const src = count <= 4 ? entree + 8 : tiff + u32(entree + 8)
    if (src + count > dv.byteLength) return null
    let s = ''
    for (let i = 0; i < count; i++) {
      const c = dv.getUint8(src + i)
      if (c === 0) break
      s += String.fromCharCode(c)
    }
    return s.trim() || null
  }

  const lireIfd = (ifd, tagsVoulus) => {
    if (ifd + 2 > dv.byteLength) return {}
    const n = u16(ifd)
    const out = {}
    for (let i = 0; i < n; i++) {
      const entree = ifd + 2 + i * 12
      if (entree + 12 > dv.byteLength) break
      const tag = u16(entree)
      if (tagsVoulus.has(tag)) out[tag] = entree
    }
    return out
  }

  const ifd0 = tiff + u32(tiff + 4)
  const t0 = lireIfd(ifd0, new Set([TAG_MAKE, TAG_MODEL, TAG_EXIF_IFD]))
  if (t0[TAG_MAKE]) make = lireAscii(t0[TAG_MAKE])
  if (t0[TAG_MODEL]) model = lireAscii(t0[TAG_MODEL])
  if (t0[TAG_EXIF_IFD]) {
    const exifIfd = tiff + u32(t0[TAG_EXIF_IFD] + 8)
    const tE = lireIfd(exifIfd, new Set([TAG_DATETIME_ORIGINAL]))
    if (tE[TAG_DATETIME_ORIGINAL]) {
      const brut = lireAscii(tE[TAG_DATETIME_ORIGINAL])
      // "YYYY:MM:DD HH:MM:SS" → "YYYY-MM-DD HH:MM:SS" (la date EXIF met des
      // deux-points DANS la date, illisible tel quel).
      if (brut && /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/.test(brut)) {
        date = brut.slice(0, 10).replace(/:/g, '-') + brut.slice(10)
      }
    }
  }

  // "Apple iPhone 14" — dédupliqué : certains modèles répètent la marque.
  let appareil = null
  if (make && model) {
    appareil = model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`
  } else {
    appareil = model || make
  }
  return { date, appareil }
}
